import { useSearchParams, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  useLineAccessToken,
  useTriggerRichMenu,
  useUserMappingStatus,
} from "@apis";
import { useAuthContext } from "@providers/AuthProvider";
import {
  PATH,
  BYPASS_CHECK_MAPPING_STATUS,
  GRAB_APP_REDIRECT_URL,
  LINE_LIFF_OPT_IN,
} from "@configs";
import liff from "@line/liff";
import * as Sentry from "@sentry/react";
import { debugLog, DebugPage } from "utils";
import loading from "@assets/gif/loading.gif";
import { useTranslation } from "react-i18next";

export const CallBackPage: React.FC = () => {
  const [queryParameters] = useSearchParams();

  const entryParam = queryParameters.get("entry");
  const errorParam = queryParameters.get("error");
  const stateParam = queryParameters.get("state");
  const codeParam = queryParameters.get("code");
  const lineAppVersionParam = queryParameters.get("lineAppVersion");
  const isLoginWithIOS = queryParameters.get("liffClientId");

  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const { init, authorizeState, onChangeLoginMethod } = useAuthContext();

  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [userProfile, setUserProfile] = useState<string | undefined>();
  const [handleAuthStep, setHandleAuthStep] = useState("");
  const [handleMappingStatusStep, setHandleMappingStatusStep] = useState("");
  const [langFormLine, setLangFormLine] = useState("");

  const {
    data: lineAccessTokenData,
    mutate: mutateLineAccessToken,
    status: lineAccessTokenStatus,
  } = useLineAccessToken();

  const {
    data: userMappingStatusData,
    error: userMappingStatusError,
    status: statusUserMappingStatus,
  } = useUserMappingStatus(
    (lineAccessTokenData?.access_token as string) ?? accessToken,
    {
      enabled:
        (lineAccessTokenData?.access_token !== undefined ||
          accessToken !== null) &&
        !BYPASS_CHECK_MAPPING_STATUS,
    }
  );

  const { mutate: mutateTriggerRichMenu } = useTriggerRichMenu({
    onSuccess: (d) => {
      // After Trigger Update Rich Menu
      debugLog(`useTriggerRichMenu success: ${d?.message}`);
      navigate(PATH.MAPPING_ALREADY_MAPPING);
    },
    onError: (d) => {
      debugLog(`useTriggerRichMenu error: ${d?.message}`);
      navigate(PATH.MAPPING_ALREADY_MAPPING);
    },
  });

  useEffect(() => {
    if (lineAppVersionParam === undefined && isLoginWithIOS === undefined) {
      onChangeLoginMethod("QR_EMAIL");
    }
  }, [lineAppVersionParam, isLoginWithIOS, onChangeLoginMethod]);

  useEffect(() => {
    if (entryParam === "line") {
      liff
        .init({
          liffId: LINE_LIFF_OPT_IN,
        })
        .then(async () => {
          const [lang, accessToken, userProfile] = await Promise.all([
            liff.getLanguage(),
            liff.getAccessToken(),
            liff.getProfile(),
          ]);

          if (lang === "th-TH") {
            i18n.changeLanguage("th");
            localStorage.setItem("preferredLanguage", "th");
          } else {
            i18n.changeLanguage("en");
            localStorage.setItem("preferredLanguage", "en");
          }
          setLangFormLine(lang);
          setAccessToken(accessToken);
          setUserProfile(userProfile?.userId);
        })
        .catch(() => {
          // Something went wrong
        });
    }
  }, [entryParam]);

  const handleAuthorization = () => {
    if (errorParam !== null) {
      debugLog("handleAuthorization Condition 1");
      if (errorParam === "The resource owner denied the request.") {
        setHandleAuthStep("Error Condition 1");
        debugLog("Error Condition 1");

        navigate(PATH.NO_CONSENT);
      } else if (["ACCESS_DENIED", "access_denied"].includes(errorParam)) {
        setHandleAuthStep("Error Condition 2");
        debugLog("Error Condition 2");

        navigate(PATH.AUTHORIZE_TOKEN_EXCHANGE, {
          state: {
            error: errorParam,
            entry: entryParam === "line" ? "line" : "grab",
          },
        });
      } else {
        Sentry.captureMessage(`ERROR CASE: NEW ERROR FROM LINE: ${errorParam}`);
      }
    } else if (authorizeState?.state === stateParam) {
      debugLog("handleAuthorization Condition 2");

      mutateLineAccessToken({
        codeVerifier: authorizeState?.codeVerifier as string,
        authorizeCode: codeParam as string,
      });
    } else if (
      init === true &&
      (lineAppVersionParam !== null ||
        isLoginWithIOS !== null ||
        entryParam === "grab")
    ) {
      debugLog("handleAuthorization Condition 3");
      window.location.replace(
        `${GRAB_APP_REDIRECT_URL}&state=${stateParam}&code=${codeParam}`
      );
    }
  };

  const handleAccessTokenStatus = async () => {
    if (lineAccessTokenStatus === "error") {
      setHandleMappingStatusStep("condition 1");
      // Handle authorization fail
    } else if (
      lineAccessTokenStatus === "success" ||
      userProfile !== undefined
    ) {
      setHandleMappingStatusStep("condition 2");
      if (BYPASS_CHECK_MAPPING_STATUS) {
        setHandleMappingStatusStep("condition 2 | BYPASS_CHECK_MAPPING_STATUS");
        navigate(PATH.AUTHORIZE_TOKEN_EXCHANGE, {
          state: {
            token:
              lineAccessTokenData?.access_token !== undefined
                ? lineAccessTokenData?.access_token
                : accessToken !== null
                ? accessToken
                : undefined,
            entry: entryParam === "line" ? "line" : "grab",
          },
        });
      } else {
        setHandleMappingStatusStep("condition 2 | Call API");
        if (["success", "error"].includes(statusUserMappingStatus)) {
          setHandleMappingStatusStep("condition 2 | Called API");
          debugLog(`userMappingStatusData: ${userMappingStatusData}`);
          // If user has already mapped
          if (userMappingStatusData?.isRegistered) {
            setHandleMappingStatusStep("condition 2 | Called API | Registered");
            // Next step: compare if a PlatformSync time is more than 5 minutes ahead of the current time

            if (userMappingStatusData?.actions) {
              setHandleMappingStatusStep(
                "condition 2 | Called API | Registered | have actions"
              );
              if (userMappingStatusData.actions.length === 2) {
                setHandleMappingStatusStep(
                  "condition 2 | Called API | Registered | actions === 2"
                );
                const platFormSyncIndex =
                  userMappingStatusData.actions.findIndex(
                    (e) => e.type === "PlatformSync"
                  );

                // Step 1: Parse the date string to a Date object
                const dateObject = new Date(
                  userMappingStatusData.actions[platFormSyncIndex].timestamp
                );
                debugLog(`dateObject: ${dateObject}`);

                // Step 2: Get the current time
                const currentTime = new Date();
                debugLog(`currentTime: ${currentTime}`);

                // Step 3: Calculate the time difference in milliseconds
                const timeDifference =
                  currentTime.getTime() - dateObject.getTime();
                debugLog(`timeDifference: ${timeDifference}`);

                // Step 4: Check if the time difference is greater than 5 minutes (300,000 milliseconds)
                const isMoreThan5MinutesAhead = timeDifference > 300000; // 5 minutes in milliseconds
                debugLog(`isMoreThan5MinutesAhead: ${isMoreThan5MinutesAhead}`);

                if (isMoreThan5MinutesAhead) {
                  setHandleMappingStatusStep(
                    "condition 2 | Called API | Registered | actions === 2 | > 5min"
                  );
                  // The date is more than 5 minutes ahead of the current time.
                  debugLog(
                    `The date is more than 5 minutes ahead of the current time.`
                  );

                  mutateTriggerRichMenu({
                    userId:
                      (lineAccessTokenData?.user_id as string) ?? userProfile,
                    isTrigger: true,
                  });
                } else {
                  setHandleMappingStatusStep(
                    "condition 2 | Called API | Registered | actions === 2 | < 5min"
                  );
                  // The date is not more than 5 minutes ahead of the current time.
                  debugLog(
                    `The date is not more than 5 minutes ahead of the current time.`
                  );

                  mutateTriggerRichMenu({
                    userId:
                      (lineAccessTokenData?.user_id as string) ?? userProfile,
                    isTrigger: false,
                  });
                }
              }
            }
          }
          // If user has not mapped before
          else {
            setHandleMappingStatusStep(
              "condition 2 | Called API | don't register"
            );
            navigate(PATH.AUTHORIZE_TOKEN_EXCHANGE, {
              state: {
                token:
                  lineAccessTokenData?.access_token !== undefined
                    ? lineAccessTokenData?.access_token
                    : accessToken !== null
                    ? accessToken
                    : undefined,
                entry: entryParam === "line" ? "line" : "grab",
              },
            });
          }
        }
      }
    }
  };

  useEffect(() => {
    handleAuthorization();
  }, [
    init,
    entryParam,
    errorParam,
    stateParam,
    codeParam,
    authorizeState,
    lineAppVersionParam,
    isLoginWithIOS,
    mutateLineAccessToken,
    navigate,
  ]);

  useEffect(() => {
    handleAccessTokenStatus();
  }, [
    lineAccessTokenStatus,
    userMappingStatusData,
    userMappingStatusError,
    accessToken,
    lineAccessTokenData,
    mutateTriggerRichMenu,
    userProfile,
    navigate,
    entryParam,
  ]);

  return (
    <>
      <DebugPage>
        <div className="text-black">{`errorParam : ${errorParam}`}</div>
        <div className="text-black">{`stateParam : ${stateParam}`}</div>
        <div className="text-black">{`codeParam : ${codeParam}`}</div>
        <div className="text-black">{`authorizeState?.state === stateParam : ${
          authorizeState?.state === stateParam
        }`}</div>
        <div className="text-black">{`handleAuthStep : ${handleAuthStep}`}</div>
        <div className="text-black">{`langFormLine: ${langFormLine}`}</div>

        <div className="text-black">{`access token from liff : ${accessToken}`}</div>
        <div className="text-black">{`userProfile from liff: ${userProfile}`}</div>
        <div className="text-black">{`status access token from api : ${lineAccessTokenStatus}`}</div>
        <div className="text-black">{`access token from api : ${lineAccessTokenData?.access_token}`}</div>

        <div className="text-black">{`statusUserMappingStatus: ${statusUserMappingStatus}`}</div>
        <div className="text-black">{`userMappingStatusData?.isRegistered: ${userMappingStatusData?.isRegistered}`}</div>
        <div className="text-black">{`handleMappingStatusStep : ${handleMappingStatusStep}`}</div>
      </DebugPage>

      <div className="min-h-screen flex content-center items-center justify-center">
        <img src={loading} alt="loading" />
      </div>
    </>
  );
};
